import { IS_PROD } from "../config";
import {
  StyledHeader,
  StyledHeaderLogo,
  StyledHeaderNav,
  StyledHeaderMenuBar,
  StyledHeaderButtons,
  StyledHeaderButton,
  StyledMobileButtons,
  StyledLanguageSelector,
  StyledButtonGroup,
  StyledMobileActions,
  StyledLanguageToggle,
  StyledLanguageToggleButton,
} from "./styles/Header.styled";
import { extractParameters, getUtmParameter } from "../common";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export const BASE_URL = !IS_PROD
  ? "https://dev-www.goodcharlie.com"
  : "https://goodcharlie.com";
export const HELP_URL = !IS_PROD
  ? "https://help.goodcharlie.com"
  : "https://help.goodcharlie.com";
export const BLOG_URL = !IS_PROD
  ? "https://dev-www.goodcharlie.com/blog"
  : "https://goodcharlie.com/blog";

// Hook to prevent body scroll when mobile menu is open
const useBodyScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    if (isLocked) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLocked]);
};

const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden="true">
    <path d="M12.5 2.375C15.0013 5.11335 16.4228 8.66703 16.5 12.375C16.4228 16.083 15.0013 19.6366 12.5 22.375M12.5 2.375C9.99872 5.11335 8.57725 8.66703 8.5 12.375C8.57725 16.083 9.99872 19.6366 12.5 22.375M12.5 2.375C6.97715 2.375 2.5 6.85215 2.5 12.375C2.5 17.8978 6.97715 22.375 12.5 22.375M12.5 2.375C18.0228 2.375 22.5 6.85215 22.5 12.375C22.5 17.8978 18.0228 22.375 12.5 22.375M3.00002 9.375H22M3 15.375H22" stroke="#202124" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none" aria-hidden="true">
    <path d="M3.75 12H21.75M3.75 6H21.75M3.75 18H21.75" stroke="#3C4043" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 6L6 18M6 6L18 18" stroke="#3C4043" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const LanguageToggle = ({
  currentLanguage,
  onChange,
  variant = "compact",
}: {
  currentLanguage: string;
  onChange: (lng: string) => void;
  variant?: "compact" | "menu";
}) => {
  const isEs = currentLanguage.toLowerCase().startsWith("es");

  return (
    <StyledLanguageToggle $variant={variant} role="group" aria-label="Language">
      <StyledLanguageToggleButton
        type="button"
        $active={!isEs}
        $variant={variant}
        onClick={() => onChange("en")}
        aria-pressed={!isEs}
      >
        {variant === "menu" ? "English" : "EN"}
      </StyledLanguageToggleButton>
      <StyledLanguageToggleButton
        type="button"
        $active={isEs}
        $variant={variant}
        onClick={() => onChange("es")}
        aria-pressed={isEs}
      >
        {variant === "menu" ? "Español" : "ES"}
      </StyledLanguageToggleButton>
    </StyledLanguageToggle>
  );
};

const Header = ({ utms }: { utms?: string }) => {
  const queryStrings = extractParameters(window.location, "product_id");
  const currentPath = window.location.pathname;
  const utmParams = getUtmParameter();
  const appendUtm = (url: string) =>
    utmParams ? `${url}${url.includes('?') ? '&' : '?'}${utmParams}` : url;
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const { t, i18n } = useTranslation("header");
  
  // Prevent body scroll when mobile menu is open
  useBodyScrollLock(showMobileMenu);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowLanguageDropdown(false);
  };

  const currentLanguage = i18n.language || 'en';
  const languageLabel = currentLanguage === 'es' ? 'Español' : 'English';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLanguageDropdown && !target.closest('[data-language-selector]')) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageDropdown]);

  return (
    <StyledHeader>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flexShrink: 1 }}>
        <StyledHeaderLogo
          href={`${BASE_URL}/${queryStrings ? `?${queryStrings}` : ""}`}
        >
          <img src="/logo-goodcharlie.svg" alt="GoodCharlie logo wordmark" loading="lazy" />
        </StyledHeaderLogo>
        {
          !IS_PROD && <span style={{ fontSize: "12px", color: "#FF5A6A", fontWeight: "bold" }}>DEV</span>
        }
      </div>

      <StyledHeaderNav show={showMobileMenu}>
        <ul>
          <li>
            <a
              href={appendUtm("/")}
              className={currentPath === "/" ? "selected-menu" : "custom-gray"}
              onClick={() => setShowMobileMenu(false)}
            >
              {t("plans")}
            </a>
          </li>
          <li>
            <a
              href={appendUtm(`${BASE_URL}/pet-perks`)}
              rel="noreferrer"
              className={currentPath.startsWith("/pet-perks") ? "selected-menu" : "custom-gray"}
              onClick={() => setShowMobileMenu(false)}
            >
              {t("petPerks")}
            </a>
          </li>
          <li>
            <a
              href={appendUtm(`${BASE_URL}/impact`)}
              rel="noreferrer"
              className={currentPath.startsWith("/impact") ? "selected-menu" : "custom-gray"}
              onClick={() => setShowMobileMenu(false)}
            >
              {t("impact")}
            </a>
          </li>
          <li>
            <a
              href={appendUtm(`${HELP_URL}/`)}
              rel="noreferrer"
              className={currentPath.startsWith("/faqs") ? "selected-menu" : "custom-gray"}
              onClick={() => setShowMobileMenu(false)}
            >
              {t("faqs")}
            </a>
          </li>
        </ul>
        <StyledMobileButtons>
          <div style={{ width: "100%", marginBottom: "8px" }}>
            <p style={{
              margin: "0 0 0px",
              fontSize: "13px",
              fontWeight: 700,
              color: "#5f6368",
            }}>
              {t("language")}
            </p>
            <LanguageToggle
              currentLanguage={currentLanguage}
              onChange={changeLanguage}
              variant="menu"
            />
          </div>
          <StyledHeaderButton
            href={appendUtm(`${!IS_PROD ? `https://dev-myaccount.goodcharlie.com/signin` : `https://myaccount.goodcharlie.com/signin`}`)}
          >
            {t("logIn")}
          </StyledHeaderButton>
          <StyledHeaderButton
            href={appendUtm(`${!IS_PROD ? `https://dev-enrollment.goodcharlie.com` : `https://enrollment.goodcharlie.com`}`)}
            primary
          >
            {t("signUp")}
          </StyledHeaderButton>
        </StyledMobileButtons>
      </StyledHeaderNav>

      <StyledHeaderButtons>
        <div style={{ position: 'relative' }} data-language-selector>
          <StyledLanguageSelector 
            type="button" 
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          >
            <GlobeIcon />
            <span>{languageLabel}</span>
          </StyledLanguageSelector>
          {showLanguageDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              marginTop: '8px',
              minWidth: '140px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
            }}>
              <button
                onClick={() => changeLanguage('en')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: currentLanguage === 'en' ? '#f5f5f5' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontWeight: currentLanguage === 'en' ? '600' : '400',
                  fontSize: '14px',
                  color: '#202124',
                  borderRadius: '8px 8px 0 0',
                }}
              >
                English
              </button>
              <button
                onClick={() => changeLanguage('es')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: currentLanguage === 'es' ? '#f5f5f5' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontWeight: currentLanguage === 'es' ? '600' : '400',
                  fontSize: '14px',
                  color: '#202124',
                  borderRadius: '0 0 8px 8px',
                }}
              >
                Español
              </button>
            </div>
          )}
        </div>
        <StyledButtonGroup>
          <StyledHeaderButton
            href={appendUtm(`${!IS_PROD ? `https://dev-myaccount.goodcharlie.com/signin` : `https://myaccount.goodcharlie.com/signin`}`)}
          >
            {t("logIn")}
          </StyledHeaderButton>
          <StyledHeaderButton
            href={appendUtm(`${!IS_PROD ? `https://dev-enrollment.goodcharlie.com` : `https://enrollment.goodcharlie.com`}`)}
            primary
          >
            {t("signUp")}
          </StyledHeaderButton>
        </StyledButtonGroup>
      </StyledHeaderButtons>

      <StyledMobileActions>
        <LanguageToggle
          currentLanguage={currentLanguage}
          onChange={changeLanguage}
          variant="compact"
        />
        <StyledHeaderMenuBar
          active={showMobileMenu}
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          type="button"
          aria-expanded={showMobileMenu}
        >
          {showMobileMenu ? <CloseIcon /> : <MenuIcon />}
          <span className="sr-only">Menu</span>
        </StyledHeaderMenuBar>
      </StyledMobileActions>
    </StyledHeader>
  );
};

export default Header;
